/* global axios */
import ApiClient from './ApiClient';

const getTimeOffset = () => -new Date().getTimezoneOffset() / 60;

class ReportsAPI extends ApiClient {
  constructor() {
    super('reports', { accountScoped: true, apiVersion: 'v2' });
  }

  getReports({
    metric,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
  }) {
    return axios.get(`${this.url}`, {
      params: {
        metric,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  // eslint-disable-next-line default-param-last
  getSummary(since, until, type = 'account', id, groupBy, businessHours) {
    return axios.get(`${this.url}/summary`, {
      params: {
        since,
        until,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getConversationMetric(type = 'account', page = 1) {
    return axios.get(`${this.url}/conversations`, {
      params: {
        type,
        page,
      },
    });
  }

  getAgentReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/agents`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getConversationsSummaryReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/conversations_summary`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getConversationTrafficCSV({ daysBefore = 6 } = {}) {
    return axios.get(`${this.url}/conversation_traffic`, {
      params: { timezone_offset: getTimeOffset(), days_before: daysBefore },
    });
  }

  getLabelReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/labels`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getInboxReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/inboxes`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getTeamReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/teams`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getBotMetrics({ from, to } = {}) {
    return axios.get(`${this.url}/bot_metrics`, {
      params: { since: from, until: to },
    });
  }

  getPhoneReports({ page, perPage, from, to, agentIds } = {}) {
    return axios.get(`${this.url}/phone_cdrs`, {
      params: {
        page,
        per_page: perPage,
        from,
        to,
        agent_ids: agentIds,
      },
    });
  }

  getRecordingReports({
    page,
    perPage,
    from,
    to,
    status,
    roomName,
    participantName,
    agentIds,
  } = {}) {
    return axios.get(`${this.url}/recordings`, {
      params: {
        page,
        per_page: perPage,
        from: from ? Math.floor(new Date(from).getTime() / 1000) : undefined,
        to: to ? Math.floor(new Date(to).getTime() / 1000) : undefined,
        status,
        room_name: roomName,
        participant_name: participantName,
        agent_ids: agentIds,
      },
    });
  }

  getRecordingPlayUrl(id) {
    return axios.get(`${this.url}/recordings/${id}/play`, {
      params: { resolve: true },
    });
  }

  getRecordingDownloadUrl(id) {
    return axios.get(`${this.url}/recordings/${id}/download`, {
      params: { resolve: true },
    });
  }

  downloadRecordingReports({ from, to, agentIds, recordingIds } = {}) {
    return axios.get(`${this.url}/recordings/download_bulk`, {
      params: {
        from,
        to,
        agent_ids: agentIds,
        recording_ids: recordingIds,
      },
      responseType: 'blob',
    });
  }

  downloadPhoneRecordings({ from, to, agentIds, cdrIds } = {}) {
    return axios.get(`${this.url}/download_phone_recordings`, {
      params: {
        from,
        to,
        agent_ids: agentIds,
        cdr_ids: cdrIds,
      },
      responseType: 'blob',
    });
  }

  downloadConversationTranscripts({
    from,
    to,
    agentIds,
    contactIds,
    useDateRange,
    agentContactPair,
  } = {}) {
    const params = {
      from,
      to,
      use_date_range: useDateRange,
      agent_contact_pair: agentContactPair,
      agent_ids: agentIds,
    };
    if (contactIds?.length) {
      params.contact_ids = contactIds;
    }
    return axios.get(`${this.url}/download_conversation_transcripts`, {
      params,
      responseType: 'blob',
    });
  }

  synthesizeConversationTranscripts({
    from,
    to,
    agentIds,
    contactIds,
    useDateRange,
    agentContactPair,
    questions = [],
  } = {}) {
    const body = {
      from,
      to,
      use_date_range: useDateRange,
      agent_contact_pair: agentContactPair,
      agent_ids: agentIds,
      questions,
    };
    if (contactIds?.length) {
      body.contact_ids = contactIds;
    }
    return axios.post(`${this.url}/synthesize_conversation_transcripts`, body, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json, text/plain, */*',
      },
    });
  }

  synthesizeCsatResponses({
    from,
    to,
    agentIds,
    contactIds,
    useDateRange,
    agentContactPair,
    questions = [],
  } = {}) {
    const body = {
      from,
      to,
      use_date_range: useDateRange,
      agent_contact_pair: agentContactPair,
      agent_ids: agentIds,
      questions,
    };
    if (contactIds?.length) {
      body.contact_ids = contactIds;
    }
    return axios.post(`${this.url}/synthesize_csat_responses`, body, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json, text/plain, */*',
      },
    });
  }

  getBotSummary({ from, to, groupBy, businessHours } = {}) {
    return axios.get(`${this.url}/bot_summary`, {
      params: {
        since: from,
        until: to,
        type: 'account',
        group_by: groupBy,
        business_hours: businessHours,
      },
    });
  }
}

export default new ReportsAPI();
